import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useAuth } from '@/features/auth/context/authProvider'
import { FormFieldConfig } from '@/shared/components/DynamicForm/DynamicForm'
import IconButton from '@/shared/components/IconButton/IconButton'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import AccountPhotoField from '../components/AccountPhotoField'
import AccountRestaurantsField from '../components/AccountRestaurantsField'
import PasswordLinkField from '../components/PasswordLinkField'
import SignOutEverywhereField from '../components/SignOutEverywhereField'

/**
 * Settings > Account as a DynamicForm, in the order of Claude's own: the
 * profile (the photo with the name and the email beside it, as the venue's
 * logo has its name), the password, the venues, and the account itself. Only
 * the name is saved by the form; everything else is shown, or is an action of
 * its own.
 */
export const useAccountFormConfig = () => {
  const { t } = useTranslation()
  const { currentUser } = useAuth()
  const { showSnackbar } = useSnackbar()

  const copyAccountId = () =>
    navigator.clipboard.writeText(currentUser?.uid ?? '').then(
      () =>
        showSnackbar(
          t('ALERTS.ACCOUNT_ID_COPIED.TITLE'),
          t('ALERTS.ACCOUNT_ID_COPIED.DESCRIPTION'),
          'success'
        ),
      () =>
        showSnackbar(
          t('ALERTS.ACCOUNT_ID_COPY_ERROR.TITLE'),
          t('ALERTS.ACCOUNT_ID_COPY_ERROR.DESCRIPTION'),
          'error'
        )
    )

  // Firebase keeps both; an account it does not describe shows neither.
  const hasDates = !!currentUser?.metadata?.creationTime

  const fields: FormFieldConfig[] = [
    {
      name: '',
      type: 'input-group',
      alignItems: 'flex-start',
      inputs: [
        {
          name: 'photo',
          type: 'custom',
          flexGrow: 0,
          render: () => <AccountPhotoField />
        },
        {
          name: '',
          type: 'input-group',
          inputGroupDirection: 'column',
          inputs: [
            {
              name: 'displayName',
              type: 'text',
              label: t('SETTINGS.ACCOUNT.NAME'),
              placeholder: t('SETTINGS.ACCOUNT.NAME_PLACEHOLDER'),
              focused: true,
              required: true
            },
            {
              name: 'email',
              type: 'read-only',
              label: t('SETTINGS.ACCOUNT.EMAIL'),
              hint: t('SETTINGS.ACCOUNT.EMAIL_HINT')
            }
          ]
        }
      ]
    },
    {
      name: 'password',
      type: 'custom',
      render: () => <PasswordLinkField />
    },
    {
      name: '',
      type: 'input-group',
      heading: t('SETTINGS.ACCOUNT.VENUES'),
      inputGroupDirection: 'column',
      inputs: [
        {
          name: 'restaurants',
          type: 'custom',
          render: () => <AccountRestaurantsField />
        }
      ]
    },
    {
      name: '',
      type: 'input-group',
      heading: t('SETTINGS.ACCOUNT.SECTION'),
      inputGroupDirection: 'column',
      inputs: [
        {
          // For a message to support, as Claude's organisation id is.
          name: 'accountId',
          type: 'read-only',
          label: t('SETTINGS.ACCOUNT.ID'),
          hint: t('SETTINGS.ACCOUNT.ID_HINT'),
          endAdornment: (
            <IconButton
              icon="las la-copy icon-medium"
              color="primary"
              ariaLabel={t('SETTINGS.ACCOUNT.ID_COPY')}
              onClick={copyAccountId}
            />
          )
        },
        ...(hasDates
          ? [
              {
                name: '',
                type: 'input-group',
                gridColumns: 2,
                inputs: [
                  {
                    name: 'createdAt',
                    type: 'read-only',
                    label: t('SETTINGS.ACCOUNT.CREATED')
                  },
                  {
                    name: 'lastSignInAt',
                    type: 'read-only',
                    label: t('SETTINGS.ACCOUNT.LAST_SIGN_IN')
                  }
                ]
              }
            ]
          : []),
        {
          name: 'signOutEverywhere',
          type: 'custom',
          render: () => <SignOutEverywhereField />
        }
      ]
    }
  ]

  // Only the name: everything else is shown, and never sent.
  const validationSchema = z.object({
    displayName: z
      .string()
      .trim()
      .min(1, t('SETTINGS.ACCOUNT.NAME_REQUIRED'))
  })

  return { fields, validationSchema }
}

export default useAccountFormConfig
